/* eslint-disable */
import { DeleteOutlined } from '@ant-design/icons';
import {
  Button, Card,
  message,
  Popconfirm,
  Space, Spin, Table, Tooltip, Typography
} from 'antd';
import { TableProps } from 'antd/lib';
import React, {
  useMemo
} from 'react';
import {
  Controller, FormProvider, useFieldArray, useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from 'app/store';
import InputCustom from 'common/components/Input';
import { createSalaryCatetoryService, getSalaryCatetoryService } from 'common/services/extends/salaryRecords';
import { SalaryCategoryParamsTypes } from 'common/services/extends/salaryRecords/types';
import { formatNumber } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';
import { useMutation, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

interface DataType {
  key: React.Key;
  id: string;
  name: string;
  price: string;
}

type FormTypes = {
  totalListOfItems: Array<DataType>
};

const defaultFormData: FormTypes = {
  totalListOfItems: []
};

type ColumnTypes = Exclude<TableProps<DataType>['columns'], undefined>;

interface CategoryCalculateProps {
  view?: boolean;
  disableAction?: boolean
}

const CategoryCalculate: React.FC<CategoryCalculateProps> = ({
  view,
  disableAction,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const rolesUser = useAppSelector((state) => state.auth.roles);

  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';

  const method = useForm({
    defaultValues: defaultFormData,
    mode: 'onChange'
  });

  const { append, fields, remove } = useFieldArray({ name: 'totalListOfItems', control: method.control });

  const queryKey = ['salary-category-list'];

  const { isLoading } = useQuery(
    queryKey,
    () => getSalaryCatetoryService({
      _payrollPeriodId: payrollPeriodId
    }),
    {
      enabled: getPermission(rolesUser, roles.SALARY_CATEGORY_INDEX),
      onSuccess(data) {
        method.reset({
          totalListOfItems: data.map((d, index) => ({
            id: index.toString(),
            key: index,
            name: d.salaryCategoryData?.name,
            price: d.salaryCategoryData.expense?.toString()
          }))
        })
      }
    }
  );

  const { mutate: createSalaryCatetoryMutate, isLoading: createSalaryCatetoryLoading } = useMutation(
    ['createSalaryCatetory'],
    async (params: SalaryCategoryParamsTypes) => createSalaryCatetoryService(params),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
      },
      onError: () => {
        message.error(t('message.updateError'));
      },
    }
  );

  const dataS = useMemo(() => fields.map((f) => ({
    key: f.id,
    id: f.id,
    name: f.name,
    price: f.price
  } as DataType)), [fields]);

  const handleDelete = async (key: number) => {
    remove(key);
    await method.trigger();
  };

  const actionColumn = (): ColumnTypes => {
    if (!view) {
      return [
        {
          title: t('system.action'),
          dataIndex: 'action',
          width: '10%',
          align: 'center',
          render: (_, __: any, index) => (fields.length >= 1 ? (
            <Popconfirm title={t('message.confirmDeleteRecord')} disabled={!getPermission(rolesUser, roles.SALARY_CATEGORY_STORE) || disableAction} onConfirm={() => handleDelete(index)}>
              <Tooltip>
                <Button
                  disabled={!getPermission(rolesUser, roles.SALARY_CATEGORY_STORE) || disableAction}
                  icon={<DeleteOutlined />}
                  size="large"
                  type="text"
                />
              </Tooltip>
            </Popconfirm>
          ) : null),
        }
      ];
    }
    return [];
  };

  const defaultColumns: ColumnTypes = useMemo(() => [
    {
      title: 'STT',
      dataIndex: 'key',
      fixed: 'left',
      width: 50,
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: t('salary.nameOfItems'),
      dataIndex: 'name',
      width: '50%',
      align: 'center',
      render: (_, __, rowIndex) => (
        <Controller
          name={`totalListOfItems.${rowIndex}.name`}
          render={({ field, fieldState }) => (
            <InputCustom
              {...field}
              type="text"
              error={fieldState.error?.message}
              readOnly={view}
            />
          )}
        />
      )
    },
    {
      title: t('salary.priceOfItems'),
      dataIndex: 'price',
      align: 'right',
      render: (_, __, rowIndex) => (
        <Controller
          name={`totalListOfItems.${rowIndex}.price`}
          render={({ field, fieldState }) => (
            <InputCustom
              {...field}
              type="number"
              hasComma
              textAlign="right"
              error={fieldState.error?.message}
              readOnly={view}
            />
          )}
        />
      ),
    },
    ...actionColumn()
  ], [fields, view]);

  const handleAdd = () => {
    append({
      name: '',
      price: '0'
    });
  };

  const onSubmit = async () => {
    const isTigger = await method.trigger();
    if (!isTigger) return;
    const data = method.getValues();
    if (payrollPeriodId) {
      createSalaryCatetoryMutate({
        payrollPeriodId: Number(payrollPeriodId),
        categories: data.totalListOfItems.map((item) => ({
          expense: Number(item.price),
          name: item.name
        }))
      })
    }
  };

  return (
    <Spin spinning={isLoading || createSalaryCatetoryLoading}>
      <Card
        title={(
          <Typography.Title level={4}>
            {t('salary.listOfItems')}
          </Typography.Title>
        )}
        extra={!view && getPermission(rolesUser, roles.SALARY_CATEGORY_STORE) ? (
          <Space>
            <Button
              size="middle"
              onClick={onSubmit}
              disabled={createSalaryCatetoryLoading || disableAction}
              style={{ borderColor: '#34C759', color: '#34C759' }}
            >
              {t('system.save')}
            </Button>
          </Space>
        ) : null}
      >
        <FormProvider {...method}>
          <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <Table<DataType>
              rowClassName={() => 'editable-row'}
              bordered
              dataSource={dataS}
              columns={defaultColumns}
              pagination={false}
              summary={() => {
                const totalPrices = method.watch('totalListOfItems').reduce((prev, cur) => prev + (cur.price ? Number(cur.price) : 0), 0);
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}>
                        <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                          {t('salary.totalListOfItems')}
                        </Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <InputCustom
                          type="text"
                          value={formatNumber(totalPrices)}
                          bordered={false}
                          textAlign="right"
                          fontWeight="700"
                        />
                        {/* <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                          {formatNumber(totalPrices)}
                        </Typography.Text> */}
                      </Table.Summary.Cell>
                      {
                        !view && (
                          <Table.Summary.Cell index={3} />
                        )
                      }
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
            {
              !view && getPermission(rolesUser, roles.SALARY_CATEGORY_STORE) && (
                <div style={{ marginLeft: 'auto', width: 'fit-content' }}>
                  <Button onClick={handleAdd} type="primary" disabled={disableAction}>
                    {t('salary.addItems')}
                  </Button>
                </div>
              )
            }
          </Space>
        </FormProvider>
      </Card>
    </Spin>
  );
};

export default CategoryCalculate;
